import React, { useState, useEffect } from "react";
import RoomPicker from "../components/RoomPicker";
import LocationSelect from "../components/LocationSelect";
import { VIRTUAL, isVirtual, locationText } from "../utils/locations";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, LinearProgress } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useActiveTenant } from "../hooks/useActiveTenant";
import NoTenantSelected from "../components/NoTenantSelected";
import {
  fetchClassTemplateById,
  instantiateClassTemplate,
  updateClass,
  createSession,
  fetchTenantUsersByRole,
  fetchTemplateSessions,
  fetchTemplateSkillSheetAssignments,
  fetchTemplateTestAssignments,
  fetchTemplateDocumentAssignments,
  fetchChecklistsForCourse,
  fetchTestsForCourse,
  fetchCourseDocumentAssignments,
  deleteChecklist,
  deleteTest,
  removeDocumentAssignment,
} from "../api/apiClient";
import { fetchSites } from "../api/rooms";

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  ink: "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line: "#E5E7EB", line2: "var(--color-hairline-cloud)", surface2: "#F6F6F5", surface3: "var(--color-hairline-cloud)",
  field: "#FFFFFF",
  page: "#FFFFFF",
  live: "#C2410C", live50: "rgba(194,65,12,.10)",
  danger: "#B42318", danger50: "rgba(180,35,24,.08)",
  blue: "var(--color-primary)", blue50: "rgba(37,99,235,.10)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04),0 1px 3px rgba(20,24,40,.06)",
  shadowMd: "0 8px 24px -10px rgba(20,24,40,.15)",
};
function makeT(theme) {
  const p = theme.palette.primary.main;
  const s = theme.palette.secondary.main;
  const a = theme.palette.warning.main;
  return {
    ...T,
    primary: p,
    primary700: theme.palette.primary.dark,
    primary10: alpha(p, 0.06),
    primary50: alpha(p, 0.15),
    green: s,
    green50: alpha(s, 0.10),
    amber: a,
    amber50: alpha(a, 0.10),
  };
}

function catMeta(cat, t) {
  const meta = {
    Firefighter: { color: "#C2410C", bg: "rgba(194,65,12,.10)" },
    EMS:         { color: "var(--color-primary)", bg: "rgba(37,99,235,.10)" },
    Hazmat:      { color: t.amber, bg: t.amber50 },
    "Fire Officer": { color: t.primary, bg: t.primary10 },
    "Search & Rescue": { color: t.green, bg: t.green50 },
  };
  return meta[cat] || { color: t.primary, bg: t.primary10 };
}

// ── Small reusable pieces ─────────────────────────────────────────────────────
function CatIcon({ cat, size = 20 }) {
  const s = { width: size, height: size, fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  if (cat === "EMS") return <svg viewBox="0 0 24 24" style={s}><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>;
  if (cat === "Hazmat") return <svg viewBox="0 0 24 24" style={s}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
  if (cat === "Search & Rescue") return <svg viewBox="0 0 24 24" style={s}><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5"/></svg>;
  return <svg viewBox="0 0 24 24" style={s}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
}

function Ctrl({ children, sx, ...rest }) {
  const T = makeT(useTheme());
  return (
    <Box component="input" sx={{
      height: 44, px: "14px", bgcolor: T.field, border: `1.5px solid ${T.line}`,
      borderRadius: "12px", font: "inherit", fontSize: 14, fontWeight: 500,
      color: T.ink, outline: "none", width: "100%",
      transition: "border-color .14s, box-shadow .14s",
      "&:focus": { borderColor: T.primary, boxShadow: `0 0 0 3px ${T.primary10}`, bgcolor: "#fff" },
      "&::placeholder": { color: T.ink4, fontWeight: 400 },
      ...sx,
    }} {...rest} />
  );
}

function CtrlSelect({ children, sx, ...rest }) {
  const T = makeT(useTheme());
  return (
    <Box sx={{ position: "relative" }}>
      <Box component="select" sx={{
        height: 44, px: "14px", pr: "36px", bgcolor: T.field, border: `1.5px solid ${T.line}`,
        borderRadius: "12px", font: "inherit", fontSize: 14, fontWeight: 500,
        color: T.ink, outline: "none", width: "100%", appearance: "none", cursor: "pointer",
        transition: "border-color .14s, box-shadow .14s",
        "&:focus": { borderColor: T.primary, boxShadow: `0 0 0 3px ${T.primary10}`, bgcolor: "#fff" },
        ...sx,
      }} {...rest}>{children}</Box>
      <Box sx={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)",
        pointerEvents: "none", color: T.ink4, display: "flex" }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </Box>
    </Box>
  );
}

function FieldLabel({ children, required, optional }) {
  return (
    <Typography component="label" sx={{ fontSize: "11px", fontWeight: 800, letterSpacing: ".08em",
      textTransform: "uppercase", color: T.ink3, display: "block" }}>
      {children}
      {required && <Box component="span" sx={{ color: T.live, ml: "2px" }}>*</Box>}
      {optional && <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: T.ink4, ml: "4px" }}>(optional)</Box>}
    </Typography>
  );
}

function Field({ label, required, optional, hint, children }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <FieldLabel required={required} optional={optional}>{label}</FieldLabel>
      {children}
      {hint && <Typography sx={{ fontSize: 12, color: T.ink3, fontWeight: 500 }}>{hint}</Typography>}
    </Box>
  );
}

function FormCard({ icon, title, sub, children }) {
  const T = makeT(useTheme());
  return (
    <Box sx={{ bgcolor: "#fff", border: `1.5px solid ${T.line}`, borderRadius: "18px",
      boxShadow: T.shadowSm, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "13px", px: "24px", py: "20px",
        pb: "16px", borderBottom: `1px solid ${T.line2}`, bgcolor: "#fff" }}>
        <Box sx={{ width: 38, height: 38, borderRadius: "11px", bgcolor: T.primary10,
          color: T.primary, display: "grid", placeItems: "center", flexShrink: 0 }}>{icon}</Box>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, fontFamily: "var(--font-display)", color: T.ink }}>{title}</Typography>
          {sub && <Typography sx={{ fontSize: 13, color: T.ink3, fontWeight: 500, mt: "2px" }}>{sub}</Typography>}
        </Box>
      </Box>
      <Box sx={{ p: "24px", display: "flex", flexDirection: "column", gap: "18px" }}>{children}</Box>
    </Box>
  );
}

function CapStepper({ value, onChange, min = 1, max = 500, step = 5 }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", border: `1.5px solid ${T.line}`,
      borderRadius: "12px", overflow: "hidden", bgcolor: T.field }}>
      <Box component="button" type="button" onClick={() => onChange(Math.max(min, value - step))}
        sx={{ width: 44, height: 44, border: 0, bgcolor: "transparent", color: T.ink3,
          fontSize: 20, cursor: "pointer", display: "grid", placeItems: "center",
          fontFamily: "inherit", transition: "all .12s",
          "&:hover": { bgcolor: T.surface3, color: T.ink } }}>−</Box>
      <Box sx={{ width: "1px", height: 24, bgcolor: T.line2 }} />
      <Box component="input" type="number" value={value}
        onChange={e => onChange(Math.max(min, Math.min(max, parseInt(e.target.value) || min)))}
        sx={{ flex: 1, textAlign: "center", fontSize: 16, fontWeight: 800, color: T.ink,
          border: 0, bgcolor: "transparent", outline: "none", fontFamily: "inherit" }} />
      <Box sx={{ width: "1px", height: 24, bgcolor: T.line2 }} />
      <Box component="button" type="button" onClick={() => onChange(Math.min(max, value + step))}
        sx={{ width: 44, height: 44, border: 0, bgcolor: "transparent", color: T.ink3,
          fontSize: 20, cursor: "pointer", display: "grid", placeItems: "center",
          fontFamily: "inherit", transition: "all .12s",
          "&:hover": { bgcolor: T.surface3, color: T.ink } }}>+</Box>
    </Box>
  );
}

// ── Step indicator ────────────────────────────────────────────────────────────
const STEPS = [
  { label: "Class Setup", sub: "Step 1" },
  { label: "Sessions", sub: "Step 2" },
  { label: "Course Materials", sub: "Step 3" },
  { label: "Review & Publish", sub: "Step 4" },
];

function StepIndicator({ current }) {
  const T = makeT(useTheme());
  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: "32px" }}>
      {STEPS.map((s, i) => (
        <React.Fragment key={i}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: "50%", display: "grid", placeItems: "center",
              flexShrink: 0, fontSize: 13, fontWeight: 800,
              border: i < current ? `2px solid ${T.green}` : i === current ? `2px solid ${T.primary}` : `2px solid ${T.line}`,
              bgcolor: i < current ? T.green : i === current ? T.primary : "#fff",
              color: i <= current ? "#fff" : T.ink4,
              boxShadow: i === current ? `0 0 0 4px ${T.primary10}` : "none",
              transition: "all .2s",
            }}>
              {i < current
                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                : i + 1}
            </Box>
            <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column" }}>
              <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".07em",
                textTransform: "uppercase",
                color: i < current ? T.green : i === current ? T.primary : T.ink4 }}>{s.sub}</Typography>
              <Typography sx={{ fontSize: "13.5px", fontWeight: 700, fontFamily: "var(--font-display)",
                color: i < current ? T.ink2 : i === current ? T.ink : T.ink3 }}>{s.label}</Typography>
            </Box>
          </Box>
          {i < STEPS.length - 1 && (
            <Box sx={{ flex: 1, height: 2, mx: "14px", borderRadius: 1,
              bgcolor: i < current ? T.green : T.line2, transition: "background .2s" }} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

// ── Session card (step 2) ─────────────────────────────────────────────────────
function SessionCard({ sess, index, onUpdate, onRemove, defaultCap, sites = [], primaryKey = "" }) {
  const T = makeT(useTheme());
  const { type, date, start, end, meetingLink, roomId } = sess;
  const set = (k, v) => onUpdate({ ...sess, [k]: v });
  const virtualClass = isVirtual(primaryKey);
  const siteKey = sess.locKey || primaryKey; // in-person sessions default to the class's primary location

  return (
    <Box sx={{ bgcolor: "#fff", border: `1.5px solid ${T.line}`, borderRadius: "14px",
      overflow: "hidden", mb: "10px",
      "&:focus-within": { borderColor: T.primary, boxShadow: `0 0 0 3px ${T.primary10}` },
    }}>
      {/* header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "16px", py: "11px",
        borderBottom: `1px solid ${T.line2}`, bgcolor: "#fff" }}>
        <Box sx={{ width: 24, height: 24, borderRadius: "7px", bgcolor: T.primary, color: "#fff",
          fontSize: 11, fontWeight: 800, display: "grid", placeItems: "center", flexShrink: 0 }}>
          {index + 1}
        </Box>
        <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: T.ink, flex: 1 }}>
          Session {index + 1}
        </Typography>
        <Box component="button" type="button" onClick={onRemove}
          sx={{ width: 28, height: 28, borderRadius: "7px", border: 0, bgcolor: "transparent",
            color: T.ink4, cursor: "pointer", display: "grid", placeItems: "center", transition: "all .12s",
            "&:hover": { bgcolor: T.danger50, color: T.danger } }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </Box>
      </Box>

      {/* body */}
      <Box sx={{ p: "14px 16px", display: "flex", flexDirection: "column", gap: "13px" }}>
        {/* Date / Start / End */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: "10px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.ink3 }}>
              Date <Box component="span" sx={{ color: T.live }}>*</Box>
            </Typography>
            <Box component="input" type="date" value={date} onChange={e => set("date", e.target.value)}
              sx={{ height: 40, px: "12px", bgcolor: T.field, border: `1.5px solid ${T.line}`,
                borderRadius: "10px", font: "inherit", fontSize: "13.5px", fontWeight: 500,
                color: T.ink, outline: "none", width: "100%", transition: "border-color .14s",
                "&:focus": { borderColor: T.primary, boxShadow: `0 0 0 3px ${T.primary10}`, bgcolor: "#fff" },
                "&::-webkit-calendar-picker-indicator": { opacity: 0.35, cursor: "pointer" },
              }} />
          </Box>
          {["Start", "End"].map((lbl, li) => (
            <Box key={lbl} sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.ink3 }}>
                {lbl} <Box component="span" sx={{ color: T.live }}>*</Box>
              </Typography>
              <Box component="input" type="time" value={li === 0 ? start : end}
                onChange={e => set(li === 0 ? "start" : "end", e.target.value)}
                sx={{ height: 40, px: "12px", bgcolor: T.field, border: `1.5px solid ${T.line}`,
                  borderRadius: "10px", font: "inherit", fontSize: "13.5px", fontWeight: 500,
                  color: T.ink, outline: "none", width: "100%", transition: "border-color .14s",
                  "&:focus": { borderColor: T.primary, boxShadow: `0 0 0 3px ${T.primary10}`, bgcolor: "#fff" },
                }} />
            </Box>
          ))}
        </Box>

        {/* Session type */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.ink3 }}>
            Session Type <Box component="span" sx={{ color: T.live }}>*</Box>
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {[
              { val: "inperson", label: "In-Person", sub: "Physical location",
                icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> },
              { val: "virtual", label: "Virtual", sub: "Online meeting",
                icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg> },
            ].map(opt => {
              const sel = type === opt.val;
              return (
                <Box key={opt.val} role="button" aria-disabled={virtualClass && opt.val === "inperson"}
                  onClick={() => { if (!(virtualClass && opt.val === "inperson")) set("type", opt.val); }}
                  title={virtualClass && opt.val === "inperson" ? "This is a 100% virtual class" : undefined}
                  sx={{ display: "flex", alignItems: "center", gap: "9px", p: "10px 12px",
                    opacity: virtualClass && opt.val === "inperson" ? 0.4 : 1,
                    borderRadius: "10px", cursor: virtualClass && opt.val === "inperson" ? "not-allowed" : "pointer", transition: "all .13s",
                    border: sel ? `1.5px solid ${T.primary}` : `1.5px solid ${T.line}`,
                    bgcolor: sel ? T.primary10 : "#fff",
                    "&:hover": { borderColor: sel ? undefined : "var(--color-hairline-cool)" },
                  }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "grid",
                    placeItems: "center", transition: "all .13s",
                    bgcolor: sel ? T.primary : T.surface3,
                    color: sel ? "#fff" : T.ink3 }}>{opt.icon}</Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: sel ? T.primary : T.ink2 }}>{opt.label}</Typography>
                    <Typography sx={{ fontSize: 11, color: T.ink4 }}>{opt.sub}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Location / meeting link */}
        {type === "inperson" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.ink3 }}>
              Location <Box component="span" sx={{ color: T.live }}>*</Box>
            </Typography>
            <LocationSelect aria-label="Session location" value={siteKey} sites={sites}
              onChange={v => (v === VIRTUAL ? onUpdate({ ...sess, type: "virtual", roomId: null }) : onUpdate({ ...sess, locKey: v, roomId: null }))}
              sx={{ height: 40, px: "12px", bgcolor: T.field, border: `1.5px solid ${T.line}`,
                borderRadius: "10px", font: "inherit", fontSize: "13.5px", fontWeight: 500,
                color: T.ink, outline: "none", width: "100%", "&:focus": { borderColor: T.primary } }} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.ink3 }}>
              Meeting Link <Box component="span" sx={{ color: T.live }}>*</Box>
            </Typography>
            <Box component="input" type="url" value={meetingLink} placeholder="https://zoom.us/j/…"
              onChange={e => set("meetingLink", e.target.value)}
              sx={{ height: 40, px: "12px", bgcolor: T.field, border: `1.5px solid ${T.line}`,
                borderRadius: "10px", font: "inherit", fontSize: "13.5px", fontWeight: 500,
                color: T.ink, outline: "none", width: "100%", transition: "border-color .14s",
                "&:focus": { borderColor: T.primary, boxShadow: `0 0 0 3px ${T.primary10}`, bgcolor: "#fff" },
                "&::placeholder": { color: T.ink4, fontWeight: 400 },
              }} />
          </Box>
        )}

        {/* Room: only rooms that hold the class's seats; capacity is a class-level setting */}
        {type === "inperson" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.ink3 }}>
              Room <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: T.ink4 }}>· optional, rooms with {defaultCap}+ seats</Box>
            </Typography>
            <RoomPicker value={roomId ?? null} onChange={id => set("roomId", id)}
              date={date} startTime={start} endTime={end} minCapacity={defaultCap} siteId={siteKey} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Hours bar ─────────────────────────────────────────────────────────────────
function HoursBar({ sessions, targetHours }) {
  const T = makeT(useTheme());
  const totalMins = sessions.reduce((sum, s) => {
    if (!s.start || !s.end) return sum;
    const [sh, sm] = s.start.split(":").map(Number);
    const [eh, em] = s.end.split(":").map(Number);
    const diff = (eh * 60 + em) - (sh * 60 + sm);
    return sum + (diff > 0 ? diff : 0);
  }, 0);
  const total = totalMins / 60;
  const pct = targetHours ? Math.min(100, (total / targetHours) * 100) : 0;
  const over = targetHours && total > targetHours;
  const full = targetHours && Math.abs(total - targetHours) < 0.01;
  const near = targetHours && pct >= 80 && !over;
  const barColor = over ? T.danger : full ? T.green : near ? T.amber : T.primary;

  return (
    <Box sx={{ bgcolor: "#fff", border: `1.5px solid ${T.line}`, borderRadius: "14px",
      p: "14px 18px", mb: "16px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "8px" }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink3 }}>Hours scheduled</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: over ? T.danger : T.ink }}>
          {total % 1 === 0 ? total : total.toFixed(1)} hrs
          {targetHours ? ` / ${targetHours} hrs` : ""}
        </Typography>
      </Box>
      {targetHours > 0 && (
        <Box sx={{ height: 7, borderRadius: "999px", bgcolor: T.line2, overflow: "hidden" }}>
          <Box sx={{ height: "100%", borderRadius: "999px", bgcolor: barColor,
            width: `${pct}%`, transition: "width .3s, background .3s" }} />
        </Box>
      )}
    </Box>
  );
}

// ── Review blocks (step 3) ────────────────────────────────────────────────────
function ReviewBlock({ icon, title, right, children }) {
  const T = makeT(useTheme());
  return (
    <Box sx={{ bgcolor: "#fff", border: `1.5px solid ${T.line}`, borderRadius: "16px",
      overflow: "hidden", mb: "16px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "11px", px: "18px", py: "14px",
        borderBottom: `1px solid ${T.line2}`, bgcolor: "#fff" }}>
        <Box sx={{ width: 34, height: 34, borderRadius: "9px", display: "grid", placeItems: "center",
          bgcolor: T.primary10, color: T.primary, flexShrink: 0 }}>{icon}</Box>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: T.ink }}>{title}</Typography>
        {right && <Box sx={{ ml: "auto" }}>{right}</Box>}
      </Box>
      <Box sx={{ p: "16px 18px" }}>{children}</Box>
    </Box>
  );
}

function ReviewGrid({ items }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
      {items.map(item => (
        <Box key={item.label} sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".07em",
            textTransform: "uppercase", color: T.ink4 }}>{item.label}</Typography>
          <Typography sx={{ fontWeight: 600, color: T.ink,
            fontFamily: item.mono ? "'JetBrains Mono', monospace" : "inherit",
            fontSize: item.mono ? "12.5px" : "13.5px" }}>{item.value || "—"}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Utility ───────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtTime(t) {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  return `${(h % 12) || 12}:${m < 10 ? "0" + m : m} ${h >= 12 ? "PM" : "AM"}`;
}
let _sessId = 0;
function newSess() {
  return { _id: ++_sessId, type: "inperson", date: "", start: "08:00", end: "16:00", locKey: "", meetingLink: "", roomId: null };
}

// ── Foot nav ──────────────────────────────────────────────────────────────────
function FormFoot({ onBack, onNext, nextLabel, nextIcon, nextColor, nextDisabled, saving }) {
  const T = makeT(useTheme());
  const btnColor = nextColor || T.primary;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", pt: "24px",
      borderTop: `1px solid ${T.line2}`, mt: "8px" }}>
      {onBack && (
        <Box component="button" type="button" onClick={onBack}
          sx={{ height: 44, px: "20px", borderRadius: "12px", border: `1.5px solid ${T.line}`,
            bgcolor: "#fff", color: T.ink2, font: "inherit", fontSize: 14, fontWeight: 700,
            cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px",
            transition: "all .13s", "&:hover": { bgcolor: T.surface3, color: T.ink } }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Back
        </Box>
      )}
      <Box sx={{ flex: 1 }} />
      <Box component="button" type="button" onClick={onNext} disabled={nextDisabled || saving}
        sx={{ height: 44, px: "20px", borderRadius: "12px", border: 0,
          bgcolor: btnColor, color: "#fff", font: "inherit",
          fontSize: 14, fontWeight: 700, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: "8px",
          boxShadow: `0 4px 12px -4px ${alpha(btnColor, 0.45)}`,
          transition: "all .13s",
          "&:hover:not(:disabled)": { filter: "brightness(.92)" },
          "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
        }}>
        {saving ? "Publishing…" : nextLabel}
        {!saving && nextIcon}
      </Box>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ScheduleFromTemplatePage() {
  const T = makeT(useTheme());
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { tenantId: effectiveTenantId } = useActiveTenant();

  const [template, setTemplate] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState(null);
  const [error, setError] = useState("");

  // Step 1 form
  const [form, setForm] = useState({
    title: "", courseNumber: "", instructorId: "", siteId: "", capacity: 24,
    totalHours: 0, notes: "",
  });

  // Step 2 sessions
  const [sessions, setSessions] = useState([]);

  // Step 3 materials + exclusion tracking
  const [materials, setMaterials] = useState({ skillSheets: [], tests: [], documents: [] });
  const [excludedSkillSheets, setExcludedSkillSheets] = useState(new Set());
  const [excludedTests, setExcludedTests] = useState(new Set());
  const [excludedDocuments, setExcludedDocuments] = useState(new Set());

  useEffect(() => {
    if (!templateId || !effectiveTenantId) return;
    Promise.all([
      fetchClassTemplateById(templateId),
      fetchSites().catch(() => []),
      fetchTenantUsersByRole(effectiveTenantId, "INSTRUCTOR").catch(() => []),
      fetchTenantUsersByRole(effectiveTenantId, "COURSE_COORDINATOR").catch(() => []),
      fetchTemplateSessions(templateId).catch(() => []),
      fetchTemplateSkillSheetAssignments(templateId).catch(() => []),
      fetchTemplateTestAssignments(templateId).catch(() => []),
      fetchTemplateDocumentAssignments(templateId).catch(() => []),
    ]).then(([tpl, siteList, instructorUsers, coordinatorUsers, tplSessions, skillSheets, tests, documents]) => {
      setTemplate(tpl);
      if (tpl?.status && tpl.status !== "PUBLISHED") setError("Publish the template first, then create a class from it.");
      setSites(Array.isArray(siteList) ? siteList : []);
      const cap = tpl.maxStudents || 24;
      setForm(f => ({
        ...f,
        title: tpl.title || "",
        courseNumber: tpl.courseNumber || "",
        capacity: cap,
        instructorId: "",
      }));

      const seen = new Set();
      const instrList = [...(instructorUsers || []), ...(coordinatorUsers || [])].filter(u => {
        if (seen.has(u.id)) return false;
        seen.add(u.id);
        return true;
      });
      setInstructors(instrList);

      const toTimeStr = (val) => {
        if (!val) return "";
        if (/^\d{2}:\d{2}/.test(val)) return val.slice(0, 5);
        try { return new Date(val).toTimeString().slice(0, 5); } catch { return ""; }
      };

      const mapped = Array.isArray(tplSessions) && tplSessions.length > 0
        ? tplSessions.map(s => ({
            _id: ++_sessId,
            type: s.meetingLink ? "virtual" : "inperson",
            date: "",
            start: toTimeStr(s.startTime) || "08:00",
            end: toTimeStr(s.endTime) || "16:00",
            locKey: "",
            meetingLink: s.meetingLink || "",
            roomId: s.roomId ?? null,
          }))
        : [newSess()];

      setSessions(mapped);
      setMaterials({
        skillSheets: Array.isArray(skillSheets) ? skillSheets : [],
        tests: Array.isArray(tests) ? tests : [],
        documents: Array.isArray(documents) ? documents : [],
      });
    }).catch(() => setError("Failed to load template."))
      .finally(() => setLoading(false));
  }, [templateId, effectiveTenantId]);

  const addSession = () => setSessions(s => [...s, newSess()]);
  const removeSession = (id) => setSessions(s => s.filter(x => x._id !== id));
  const updateSession = (id, updated) => setSessions(s => s.map(x => x._id === id ? updated : x));

  const meta = catMeta(template?.category, T);

  const publish = async () => {
    setSaving(true);
    setError("");
    try {
      const tenantId = effectiveTenantId;
      // 1. Instantiate template → new class
      const cloned = await instantiateClassTemplate(tenantId, templateId);
      // 2. Update cloned class with wizard form data
      await updateClass(tenantId, cloned.id, {
        title: form.title,
        courseNumber: form.courseNumber,
        capacity: form.capacity,
        availableSeats: form.capacity,
        totalHours: form.totalHours || null,
        leadInstructorId: form.instructorId || null,
        instructorId: form.instructorId || null,
        ...(form.notes ? { description: (cloned.description ? cloned.description + "\n\n" : "") + "Notes: " + form.notes } : {}),
      });
      // 3. Create sessions
      for (const sess of sessions) {
        if (!sess.date) continue;
        const virtual = isVirtual(form.siteId) || sess.type === "virtual";
        await createSession(cloned.id, {
          sessionDate: sess.date,
          startTime: sess.start,
          endTime: sess.end,
          ...(virtual
            ? { location: "Virtual", sessionType: "VIRTUAL", ...(sess.meetingLink ? { meetingLink: sess.meetingLink } : {}) }
            : { location: locationText(sess.locKey || form.siteId, sites), sessionType: "PHYSICAL", ...(sess.roomId ? { roomId: sess.roomId } : {}) }),
        });
      }
      // 4. Remove excluded materials from cloned class
      const ssExcludedTitles = new Set(
        materials.skillSheets.filter(ss => excludedSkillSheets.has(ss.id)).map(ss => ss.title || ss.name)
      );
      const testExcludedTitles = new Set(
        materials.tests.filter(t => excludedTests.has(t.id)).map(t => t.title || t.name)
      );
      const docExcludedTitles = new Set(
        materials.documents.filter(d => excludedDocuments.has(d.id)).map(d => d.document?.title || d.document?.name || d.title)
      );
      if (ssExcludedTitles.size || testExcludedTitles.size || docExcludedTitles.size) {
        const [clonedSheets, clonedTests, clonedDocs] = await Promise.all([
          ssExcludedTitles.size ? fetchChecklistsForCourse(cloned.id).catch(() => []) : Promise.resolve([]),
          testExcludedTitles.size ? fetchTestsForCourse(cloned.id).catch(() => []) : Promise.resolve([]),
          docExcludedTitles.size ? fetchCourseDocumentAssignments(cloned.id).catch(() => []) : Promise.resolve([]),
        ]);
        await Promise.all([
          ...(clonedSheets || []).filter(ss => ssExcludedTitles.has(ss.title || ss.name)).map(ss => deleteChecklist(ss.id).catch(() => {})),
          ...(clonedTests || []).filter(t => testExcludedTitles.has(t.title || t.name)).map(t => deleteTest(t.id).catch(() => {})),
          ...(clonedDocs || []).filter(d => docExcludedTitles.has(d.document?.title || d.document?.name || d.title)).map(d => removeDocumentAssignment(cloned.id, d.id).catch(() => {})),
        ]);
      }
      setPublished({ id: cloned.id, title: form.title });
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to publish class. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!effectiveTenantId) return (
    <Box sx={{ bgcolor: T.page, minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", p: 4 }}>
      <NoTenantSelected message="Select a training center before scheduling from a template. Use the tenant switcher in the sidebar." />
    </Box>
  );

  if (loading) return (
    <Box sx={{ bgcolor: T.page, minHeight: "100vh", pt: "56px" }}>
      <LinearProgress sx={{ bgcolor: T.primary10, "& .MuiLinearProgress-bar": { bgcolor: T.primary } }} />
    </Box>
  );

  if (error && !template) return (
    <Box sx={{ bgcolor: T.page, minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", p: 4 }}>
      <Typography color="error">{error}</Typography>
    </Box>
  );

  // ── Success screen ──────────────────────────────────────────────────────────
  if (published) {
    return (
      <Box sx={{ bgcolor: T.page, minHeight: "100vh" }}>
        <Box sx={{ maxWidth: 860, mx: "auto", px: "36px", pt: "60px" }}>
          <Box sx={{ bgcolor: "#fff", border: `1.5px solid ${T.line}`, borderRadius: "18px",
            boxShadow: T.shadowSm, display: "flex", flexDirection: "column", alignItems: "center",
            gap: "22px", p: "64px 32px", textAlign: "center" }}>
            <Box sx={{ width: 72, height: 72, borderRadius: "50%", bgcolor: T.green50,
              display: "grid", placeItems: "center", color: T.green }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.025em", color: T.ink }}>Class Published!</Typography>
              <Typography sx={{ fontSize: 15, color: T.ink3, mt: "10px", maxWidth: "36ch", mx: "auto", lineHeight: 1.6 }}>
                "{published.title}" is now live. Enrollment is open.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center", mt: "8px" }}>
              <Box component="button" onClick={() => navigate("/course-management")}
                sx={{ height: 44, px: "20px", borderRadius: "12px", border: `1.5px solid ${T.line}`,
                  bgcolor: "#fff", color: T.ink2, font: "inherit", fontSize: 14, fontWeight: 700,
                  cursor: "pointer", transition: "all .13s", "&:hover": { bgcolor: T.surface3 } }}>
                Back to Courses
              </Box>
              <Box component="button" onClick={() => navigate(`/course-management/${published.id}`)}
                sx={{ height: 44, px: "20px", borderRadius: "12px", border: 0, bgcolor: T.primary,
                  color: "#fff", font: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer",
                  boxShadow: `0 4px 12px -4px ${alpha(T.primary, 0.4)}`,
                  transition: "all .13s", "&:hover": { bgcolor: T.primary700 } }}>
                View Class
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: T.page, minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 860, mx: "auto", px: { xs: "16px", sm: "36px" }, pt: "32px", pb: "100px" }}>

        {/* Breadcrumb */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "24px",
          fontSize: 13, fontWeight: 600, color: T.ink3 }}>
          <Box component="button" onClick={() => navigate("/course-management")}
            sx={{ border: 0, bgcolor: "transparent", color: T.ink3, cursor: "pointer",
              font: "inherit", fontSize: 13, fontWeight: 600, p: 0,
              "&:hover": { color: T.primary } }}>
            Manage Courses
          </Box>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          <Box component="button" onClick={() => navigate("/course-management")}
            sx={{ border: 0, bgcolor: "transparent", color: T.ink3, cursor: "pointer",
              font: "inherit", fontSize: 13, fontWeight: 600, p: 0,
              "&:hover": { color: T.primary } }}>
            Templates
          </Box>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: T.ink }}>Schedule Class</Typography>
        </Box>

        {/* Step indicator */}
        <StepIndicator current={step} />

        {/* Template banner */}
        {template && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px", bgcolor: "#fff",
            border: `1.5px solid ${T.line}`, borderRadius: "16px", p: "16px 20px",
            mb: "28px", boxShadow: T.shadowSm }}>
            <Box sx={{ width: 46, height: 46, borderRadius: "13px", display: "grid",
              placeItems: "center", flexShrink: 0, bgcolor: meta.bg, color: meta.color }}>
              <CatIcon cat={template.category} size={22} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".1em",
                textTransform: "uppercase", color: T.ink4, mb: "3px" }}>
                Scheduling from Template
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: T.ink,
                fontFamily: "var(--font-display)", lineHeight: 1.2 }}>{template.title}</Typography>
              <Box sx={{ display: "flex", gap: "16px", mt: "6px", flexWrap: "wrap" }}>
                {template.category && (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.ink3, display: "flex", alignItems: "center", gap: "5px" }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: meta.color, flexShrink: 0 }} />
                    {template.category}
                  </Typography>
                )}
                {template.maxStudents && (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.ink3 }}>
                    {template.maxStudents} default capacity
                  </Typography>
                )}
              </Box>
            </Box>
            {template.courseNumber && (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 11,
                fontWeight: 700, px: "12px", py: "5px", borderRadius: "999px",
                bgcolor: T.surface3, color: T.ink3, border: `1px solid ${T.line}`,
                fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
                #{template.courseNumber}
              </Box>
            )}
          </Box>
        )}

        {/* ═══ STEP 0: CLASS SETUP ═══ */}
        {step === 0 && (
          <>
            <FormCard
              title="Class Setup"
              sub="Configure the basic details for this class instance."
              icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>}
            >
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "16px" }}>
                <Field label="Class Title" required>
                  <Ctrl type="text" placeholder="Class title" value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                </Field>
                <Field label="Course Code">
                  <Ctrl type="text" placeholder="e.g. FF050" value={form.courseNumber}
                    sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "13px" }}
                    onChange={e => setForm(f => ({ ...f, courseNumber: e.target.value }))} />
                </Field>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "16px" }}>
                <Field label="Primary Instructor" required>
                  <CtrlSelect value={form.instructorId}
                    onChange={e => setForm(f => ({ ...f, instructorId: e.target.value }))}>
                    <option value="">Select instructor…</option>
                    {instructors.map(i => (
                      <option key={i.id} value={i.id}>{i.fullName || i.username}</option>
                    ))}
                  </CtrlSelect>
                </Field>
                <Field label="Primary Location" required>
                  <LocationSelect aria-label="Primary location" value={form.siteId} sites={sites}
                    onChange={v => {
                      setForm(f => ({ ...f, siteId: v }));
                      // A 100% virtual class can't have in-person sessions.
                      if (isVirtual(v)) setSessions(ss => ss.map(x => ({ ...x, type: "virtual", roomId: null })));
                    }}
                    sx={{ height: 44, px: "14px", bgcolor: T.field, border: `1.5px solid ${T.line}`, borderRadius: "12px",
                      font: "inherit", fontSize: 14, fontWeight: 500, color: T.ink, outline: "none", width: "100%" }} />
                  {isVirtual(form.siteId) && (
                    <Typography sx={{ fontSize: 11.5, color: T.ink4, mt: "5px" }}>100% virtual class — every session will be virtual.</Typography>
                  )}
                </Field>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "16px" }}>
                <Field label="Max Enrollment" required>
                  <CapStepper value={form.capacity} onChange={v => setForm(f => ({ ...f, capacity: v }))} />
                </Field>
                <Field label="Total Course Hours" hint="Used to track session scheduling progress">
                  <Ctrl type="number" placeholder="0" value={form.totalHours || ""}
                    min={0}
                    onChange={e => setForm(f => ({ ...f, totalHours: parseInt(e.target.value) || 0 }))} />
                </Field>
              </Box>

              <Field label="Internal Notes" optional>
                <Box component="textarea" placeholder="Special requirements, coordinator notes, or reminders for this run…"
                  value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  sx={{ p: "12px 14px", bgcolor: T.field, border: `1.5px solid ${T.line}`,
                    borderRadius: "12px", font: "inherit", fontSize: 14, fontWeight: 500,
                    color: T.ink, outline: "none", width: "100%", resize: "vertical",
                    transition: "border-color .14s, box-shadow .14s",
                    "&:focus": { borderColor: T.primary, boxShadow: `0 0 0 3px ${T.primary10}`, bgcolor: "#fff" },
                    "&::placeholder": { color: T.ink4, fontWeight: 400 },
                  }} />
              </Field>
            </FormCard>

            <FormFoot
              onBack={() => navigate("/course-management")}
              onNext={() => setStep(1)}
              nextDisabled={!form.title || !form.instructorId || !form.siteId}
              nextLabel="Next: Schedule Sessions"
              nextIcon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>}
            />
          </>
        )}

        {/* ═══ STEP 1: SESSIONS ═══ */}
        {step === 1 && (
          <>
            <HoursBar sessions={sessions} targetHours={form.totalHours} />

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: ".08em",
                  textTransform: "uppercase", color: T.ink4 }}>Sessions</Typography>
                <Box sx={{ fontSize: 11, fontWeight: 800, px: "8px", py: "2px", borderRadius: "999px",
                  bgcolor: T.primary10, color: T.primary }}>{sessions.length}</Box>
              </Box>
              <Box component="button" type="button" onClick={addSession}
                sx={{ display: "inline-flex", alignItems: "center", gap: "6px", height: 34,
                  px: "14px", borderRadius: "9px", border: `1.5px solid ${T.line}`, bgcolor: "#fff",
                  font: "inherit", fontSize: 13, fontWeight: 700, color: T.primary, cursor: "pointer",
                  transition: "all .13s",
                  "&:hover": { borderColor: T.primary, bgcolor: T.primary10 } }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Session
              </Box>
            </Box>

            {sessions.length === 0 && (
              <Box sx={{ bgcolor: "#fff", border: `1.5px solid ${T.line}`, borderRadius: "14px",
                p: "32px 24px", textAlign: "center", mb: "10px" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink3 }}>
                  No sessions yet — add at least one.
                </Typography>
              </Box>
            )}

            {sessions.map((sess, i) => (
              <SessionCard key={sess._id} sess={sess} index={i}
                sites={sites} primaryKey={form.siteId}
                onUpdate={updated => updateSession(sess._id, updated)}
                onRemove={() => removeSession(sess._id)}
                defaultCap={form.capacity} />
            ))}

            <FormFoot
              onBack={() => setStep(0)}
              onNext={() => setStep(2)}
              nextDisabled={sessions.length === 0}
              nextLabel="Next: Course Materials"
              nextIcon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>}
            />
          </>
        )}

        {/* ═══ STEP 2: COURSE MATERIALS ═══ */}
        {step === 2 && (
          <>
            {(materials.skillSheets.length + materials.tests.length + materials.documents.length) === 0 ? (
              <Box sx={{ bgcolor: "#fff", border: `1.5px solid ${T.line}`, borderRadius: "16px",
                p: "40px 24px", textAlign: "center", mb: "16px" }}>
                <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: T.primary10,
                  display: "grid", placeItems: "center", mx: "auto", mb: "16px", color: T.primary }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.ink, mb: "6px" }}>No course materials assigned</Typography>
                <Typography sx={{ fontSize: 13, color: T.ink3 }}>This template has no skill sheets, tests, or documents. You can add them after publishing.</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ bgcolor: T.primary10, border: `1px solid ${alpha(T.primary, 0.15)}`, borderRadius: "12px",
                  p: "12px 16px", mb: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.primary }}>
                    Uncheck any materials you don't want carried over to the new class.
                  </Typography>
                </Box>

                {materials.skillSheets.length > 0 && (
                  <ReviewBlock
                    title="Skill Sheets"
                    icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>}
                    right={
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink3 }}>
                        {materials.skillSheets.length - excludedSkillSheets.size} / {materials.skillSheets.length}
                      </Typography>
                    }
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {materials.skillSheets.map((ss, i) => {
                        const excluded = excludedSkillSheets.has(ss.id);
                        const toggle = () => setExcludedSkillSheets(prev => {
                          const next = new Set(prev);
                          excluded ? next.delete(ss.id) : next.add(ss.id);
                          return next;
                        });
                        return (
                          <Box key={ss.id || i} onClick={toggle}
                            sx={{ display: "flex", alignItems: "center", gap: "10px",
                              p: "10px 14px", bgcolor: excluded ? T.surface3 : T.surface2,
                              border: `1px solid ${excluded ? T.line : T.line2}`, borderRadius: "10px",
                              cursor: "pointer", opacity: excluded ? 0.55 : 1, transition: "all .15s",
                              "&:hover": { borderColor: T.primary } }}>
                            <Box sx={{ width: 20, height: 20, borderRadius: "5px", flexShrink: 0,
                              border: `2px solid ${excluded ? T.ink4 : T.primary}`,
                              bgcolor: excluded ? "transparent" : T.primary,
                              display: "grid", placeItems: "center", color: "#fff", transition: "all .15s" }}>
                              {!excluded && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                            </Box>
                            <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: excluded ? T.surface3 : T.primary10,
                              display: "grid", placeItems: "center", color: excluded ? T.ink4 : T.primary, flexShrink: 0 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: "13.5px", fontWeight: 700,
                                color: excluded ? T.ink4 : T.ink,
                                textDecoration: excluded ? "line-through" : "none" }}>
                                {ss.title || ss.name || `Skill Sheet #${ss.id}`}
                              </Typography>
                              {ss.requiredForPass && !excluded && (
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.amber }}>Required for pass</Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </ReviewBlock>
                )}

                {materials.tests.length > 0 && (
                  <ReviewBlock
                    title="Written Tests"
                    icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>}
                    right={
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink3 }}>
                        {materials.tests.length - excludedTests.size} / {materials.tests.length}
                      </Typography>
                    }
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {materials.tests.map((t, i) => {
                        const excluded = excludedTests.has(t.id);
                        const toggle = () => setExcludedTests(prev => {
                          const next = new Set(prev);
                          excluded ? next.delete(t.id) : next.add(t.id);
                          return next;
                        });
                        return (
                          <Box key={t.id || i} onClick={toggle}
                            sx={{ display: "flex", alignItems: "center", gap: "10px",
                              p: "10px 14px", bgcolor: excluded ? T.surface3 : T.surface2,
                              border: `1px solid ${excluded ? T.line : T.line2}`, borderRadius: "10px",
                              cursor: "pointer", opacity: excluded ? 0.55 : 1, transition: "all .15s",
                              "&:hover": { borderColor: T.primary } }}>
                            <Box sx={{ width: 20, height: 20, borderRadius: "5px", flexShrink: 0,
                              border: `2px solid ${excluded ? T.ink4 : T.primary}`,
                              bgcolor: excluded ? "transparent" : T.primary,
                              display: "grid", placeItems: "center", color: "#fff", transition: "all .15s" }}>
                              {!excluded && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                            </Box>
                            <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: excluded ? T.surface3 : T.primary10,
                              display: "grid", placeItems: "center", color: excluded ? T.ink4 : T.primary, flexShrink: 0 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: "13.5px", fontWeight: 700,
                                color: excluded ? T.ink4 : T.ink,
                                textDecoration: excluded ? "line-through" : "none" }}>
                                {t.title || t.name || `Test #${t.id}`}
                              </Typography>
                              {t.passingScore != null && !excluded && (
                                <Typography sx={{ fontSize: 11, color: T.ink3 }}>Passing score: {t.passingScore}%</Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </ReviewBlock>
                )}

                {materials.documents.length > 0 && (
                  <ReviewBlock
                    title="Documents"
                    icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
                    right={
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink3 }}>
                        {materials.documents.length - excludedDocuments.size} / {materials.documents.length}
                      </Typography>
                    }
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {materials.documents.map((d, i) => {
                        const excluded = excludedDocuments.has(d.id);
                        const toggle = () => setExcludedDocuments(prev => {
                          const next = new Set(prev);
                          excluded ? next.delete(d.id) : next.add(d.id);
                          return next;
                        });
                        const title = d.document?.title || d.document?.name || d.title || `Document #${d.documentId || d.id}`;
                        return (
                          <Box key={d.id || i} onClick={toggle}
                            sx={{ display: "flex", alignItems: "center", gap: "10px",
                              p: "10px 14px", bgcolor: excluded ? T.surface3 : T.surface2,
                              border: `1px solid ${excluded ? T.line : T.line2}`, borderRadius: "10px",
                              cursor: "pointer", opacity: excluded ? 0.55 : 1, transition: "all .15s",
                              "&:hover": { borderColor: T.primary } }}>
                            <Box sx={{ width: 20, height: 20, borderRadius: "5px", flexShrink: 0,
                              border: `2px solid ${excluded ? T.ink4 : T.primary}`,
                              bgcolor: excluded ? "transparent" : T.primary,
                              display: "grid", placeItems: "center", color: "#fff", transition: "all .15s" }}>
                              {!excluded && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                            </Box>
                            <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: excluded ? T.surface3 : T.primary10,
                              display: "grid", placeItems: "center", color: excluded ? T.ink4 : T.primary, flexShrink: 0 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            </Box>
                            <Typography sx={{ fontSize: "13.5px", fontWeight: 700, flex: 1,
                              color: excluded ? T.ink4 : T.ink,
                              textDecoration: excluded ? "line-through" : "none" }}>
                              {title}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </ReviewBlock>
                )}
              </>
            )}

            <FormFoot
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
              nextLabel="Next: Review & Publish"
              nextIcon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>}
            />
          </>
        )}

        {/* ═══ STEP 3: REVIEW ═══ */}
        {step === 3 && (
          <>
            {error && (
              <Box sx={{ bgcolor: "rgba(180,35,24,.06)", border: `1px solid rgba(180,35,24,.2)`,
                borderRadius: "12px", p: "12px 16px", mb: "16px" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.danger }}>{error}</Typography>
              </Box>
            )}

            <ReviewBlock
              title="Class Details"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/></svg>}
            >
              <ReviewGrid items={[
                { label: "Class Title", value: form.title },
                { label: "Course Code", value: form.courseNumber, mono: true },
                { label: "Instructor", value: instructors.find(i => String(i.id) === String(form.instructorId))?.fullName || instructors.find(i => String(i.id) === String(form.instructorId))?.username || form.instructorId || "—" },
                { label: "Location", value: locationText(form.siteId, sites) || "—" },
                { label: "Max Enrollment", value: form.capacity ? `${form.capacity} students` : "—" },
                { label: "Total Hours", value: form.totalHours ? `${form.totalHours} hrs` : "Not set" },
              ]} />
              {form.notes && (
                <Box sx={{ mt: "12px", pt: "12px", borderTop: `1px solid ${T.line2}` }}>
                  <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".07em",
                    textTransform: "uppercase", color: T.ink4, mb: "4px" }}>Notes</Typography>
                  <Typography sx={{ fontSize: "13.5px", fontWeight: 500, color: T.ink2 }}>{form.notes}</Typography>
                </Box>
              )}
            </ReviewBlock>

            {(materials.skillSheets.length + materials.tests.length + materials.documents.length) > 0 && (() => {
              const includedSheets = materials.skillSheets.filter(ss => !excludedSkillSheets.has(ss.id));
              const includedTests = materials.tests.filter(t => !excludedTests.has(t.id));
              const includedDocs = materials.documents.filter(d => !excludedDocuments.has(d.id));
              const total = includedSheets.length + includedTests.length + includedDocs.length;
              return (
                <ReviewBlock
                  title="Course Materials"
                  icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
                  right={<Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink3 }}>{total} item{total !== 1 ? "s" : ""}</Typography>}
                >
                  {total === 0 ? (
                    <Typography sx={{ fontSize: 13, color: T.ink4, fontWeight: 500 }}>All materials removed — none will carry over.</Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {includedSheets.map((ss, i) => (
                        <Box key={ss.id || i} sx={{ fontSize: 12, fontWeight: 600, px: "10px", py: "4px",
                          borderRadius: "999px", bgcolor: T.primary10, color: T.primary, border: `1px solid ${alpha(T.primary, 0.15)}` }}>
                          {ss.title || ss.name || `Skill Sheet #${ss.id}`}
                        </Box>
                      ))}
                      {includedTests.map((t, i) => (
                        <Box key={t.id || i} sx={{ fontSize: 12, fontWeight: 600, px: "10px", py: "4px",
                          borderRadius: "999px", bgcolor: T.blue50, color: T.blue, border: `1px solid rgba(37,99,235,.15)` }}>
                          {t.title || t.name || `Test #${t.id}`}
                        </Box>
                      ))}
                      {includedDocs.map((d, i) => (
                        <Box key={d.id || i} sx={{ fontSize: 12, fontWeight: 600, px: "10px", py: "4px",
                          borderRadius: "999px", bgcolor: T.surface3, color: T.ink2, border: `1px solid ${T.line}` }}>
                          {d.document?.title || d.document?.name || d.title || `Document #${d.documentId || d.id}`}
                        </Box>
                      ))}
                    </Box>
                  )}
                </ReviewBlock>
              );
            })()}

            <ReviewBlock
              title="Scheduled Sessions"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
              right={<Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink3 }}>{sessions.length} session{sessions.length !== 1 ? "s" : ""}</Typography>}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {sessions.map((sess, i) => (
                  <Box key={sess._id} sx={{ display: "flex", alignItems: "center", gap: "12px",
                    p: "10px 14px", bgcolor: T.surface2, border: `1px solid ${T.line2}`,
                    borderRadius: "10px" }}>
                    <Box sx={{ width: 24, height: 24, borderRadius: "7px", bgcolor: T.primary,
                      color: "#fff", fontSize: 11, fontWeight: 800, display: "grid",
                      placeItems: "center", flexShrink: 0 }}>{i + 1}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: T.ink }}>
                        {sess.date ? fmtDate(sess.date) : "Date TBD"}
                        {sess.start && sess.end ? ` · ${fmtTime(sess.start)} – ${fmtTime(sess.end)}` : ""}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: T.ink3, fontWeight: 500 }}>
                        {sess.type === "virtual" || isVirtual(form.siteId)
                          ? "Virtual"
                          : (locationText(sess.locKey || form.siteId, sites) || "Location TBD")}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </ReviewBlock>

            <FormFoot
              onBack={() => setStep(2)}
              onNext={publish}
              saving={saving}
              nextDisabled={!!template?.status && template.status !== "PUBLISHED"}
              nextLabel="Publish Class"
              nextColor={T.green}
              nextIcon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
            />
          </>
        )}

      </Box>
    </Box>
  );
}
